// src/services/fxService.ts
import apiClient from "./apiClient";
import { getApiErrorMessage } from "../utils/apiError";
import { FxRate, FxTransactionsApiResponse } from "../types/fx";

const RATE_PATH = "rate";

export interface FxRateHistoryResult {
  results: FxRate[];
  pagination: {
    totalItems?: number;
    currentPage?: number;
    totalPages?: number;
    limit?: number;
  };
}

export const getRateHistory = async (
  from: string,
  to: string,
  page = 1,
  limit = 10
): Promise<FxRateHistoryResult> => {
  try {
    const response = await apiClient.get(`${RATE_PATH}/history`, {
      params: { fromCurrency: from, toCurrency: to, page, limit },
    });
    const body = response.data as {
      data?: FxRate[];
      results?: FxRate[];
      pagination?: FxRateHistoryResult["pagination"];
    };

    return {
      results: body.data ?? body.results ?? [],
      pagination: body.pagination ?? {},
    };
  } catch (error) {

    throw new Error(getApiErrorMessage(error, "Failed to fetch FX rate history"));
  }
};

export const createRate = async (rateData: {
  fromCurrency: string;
  toCurrency: string;
  buyRate: number;
  sellRate: number;
}): Promise<FxRate> => {
  try {
    const response = await apiClient.post<FxRate>(`${RATE_PATH}/create`, rateData);
    return response.data;
  } catch (error) {

    throw new Error(getApiErrorMessage(error, "Failed to create FX rate"));
  }
};

export const updateRate = async (rateData: {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  buyRate: number;
  sellRate: number;
}): Promise<FxRate> => {
  try {
    const { id: _id, ...updatePayload } = rateData;
    const response = await apiClient.patch<FxRate>(
      `${RATE_PATH}/update`,
      updatePayload
    );
    return response.data;
  } catch (error) {

    throw new Error(getApiErrorMessage(error, "Failed to update FX rate"));
  }
};

export const getFxTransactions = async (
  page = 1,
  limit = 10,
  _startDate?: string,
  _endDate?: string,
  _search?: string
): Promise<FxTransactionsApiResponse> => {
  try {
    const params: Record<string, string | number> = { page, limit };
    // This endpoint currently supports pagination only. UI filtering is page-local.

    const response = await apiClient.get(`${RATE_PATH}/all-exchanges`, {
      params,
    });
    const body = response.data as Record<string, unknown>;
    const results = (body?.results ?? body?.data ?? []) as FxTransactionsApiResponse["results"];
    const pagination = body?.pagination as FxTransactionsApiResponse["pagination"];
    return {
      success: true,
      results,
      pagination,
    };
  } catch (error) {

    throw new Error(getApiErrorMessage(error, "Failed to fetch FX transactions"));
  }
};

export const downloadFxTransactionsPDF = async (): Promise<Blob> => {
  try {
    const response = await apiClient.get(`${RATE_PATH}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {

    throw new Error(getApiErrorMessage(error, "Failed to download PDF"));
  }
};

export async function toggleRate(id: string) {
  return (await apiClient.patch(`rate/${encodeURIComponent(id)}/toggle`)).data;
}
export async function deleteRate(id: string) {
  return (await apiClient.delete(`rate/${encodeURIComponent(id)}`)).data;
}
export async function getUserFxTransactions(userId: string, page = 1, limit = 10): Promise<FxTransactionsApiResponse> {
  const { data } = await apiClient.get(`rate/${encodeURIComponent(userId)}/user-fx`, { params: { page, limit } });
  return { success: true, results: data.data, pagination: data.pagination };
}
